import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import appStoreDownload from '../assets/app_store_download.svg';
import playStoreDownload from '../assets/play_store_download.svg';
import screenshot from '../assets/screenshot.png';

function Home() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `
          linear-gradient(135deg, #0D1013 0%, #1a1f24 100%)
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 185, 93, 0.08) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(228, 193, 141, 0.08) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        },
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box
          sx={{
            pt: { xs: 6, md: 8 },
            pb: { xs: 6, md: 8 },
            position: 'relative',
            zIndex: 2,
          }}
        >
          <Grid container spacing={2} alignItems="center" justifyContent="center">
            {/* Left Column - Content */}
            <Grid item xs={12} md={5}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography
                  variant="h1"
                  sx={{
                    mb: 4,
                    color: 'white',
                    fontSize: { xs: '1.8rem', md: '3rem' },
                    fontWeight: 400,
                    lineHeight: 1.2,
                    fontFamily: '"SF Pro Display", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
                  }}
                >
                  Track Your Life's Journey with Elegant Widgets
                </Typography>
                
                <Typography 
                  variant="h5" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 6, 
                    fontSize: { xs: '1rem', md: '1.2rem' },
                    lineHeight: 1.5,
                  }}
                >
                  Beautiful home screen widgets that blend seamlessly with your device's theme, helping you visualize your life's progress.
                </Typography>

                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  justifyContent: { xs: 'center', md: 'flex-start' },
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'center', md: 'flex-start' },
                }}>
                  <Box
                    component="a"
                    href="https://apps.apple.com/us/app/lifetime-timer/id6747040246?platform=iphone"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      transition: 'transform 0.3s ease, filter 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        filter: 'brightness(1.1)',
                      },
                      '& img': {
                        display: 'block',
                        width: 'auto',
                        height: '52px',
                        maxWidth: 'none',
                        transform: 'translateZ(0)', // Force GPU acceleration
                      },
                    }}
                  >
                    <img
                      src={appStoreDownload}
                      alt="Download on App Store"
                    />
                  </Box>
                  <Box
                    component="a"
                    href="https://play.google.com/store/apps/details?id=com.hundredgb.lifetimetimer"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'inline-block',
                      transition: 'transform 0.3s ease, filter 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        filter: 'brightness(1.1)',
                      },
                      '& img': {
                        display: 'block',
                        width: 'auto',
                        height: '52px',
                        maxWidth: 'none',
                        transform: 'translateZ(0)', // Force GPU acceleration
                      },
                    }}
                  >
                    <img
                      src={playStoreDownload}
                      alt="Get it on Google Play"
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Right Column - Screenshot */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'center', md: 'center' },
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    maxWidth: '250px',
                    width: '100%',
                    position: 'relative',
                  }}
                >
                  <img
                    src={screenshot}
                    alt="Lifetime Timer App Screenshot"
                    style={{
                      width: '100%',
                      height: 'auto',
                      borderRadius: '12px',
                      display: 'block',
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Features Section */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 185, 93, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 185, 93, 0.2)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                  Beautiful Widgets
                </Typography>
                <Typography color="text.primary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.6 }}>
                  Track your life's progress with elegant home screen widgets that blend seamlessly with your device's theme.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 185, 93, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 185, 93, 0.2)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                  Offline First
                </Typography>
                <Typography color="text.primary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.6 }}>
                  Works completely offline. Your data stays on your device, ensuring privacy and reliability.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card
              sx={{
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 185, 93, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(255, 185, 93, 0.2)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontSize: { xs: '1.1rem', md: '1.25rem' }, fontWeight: 600 }}>
                  Customizable
                </Typography>
                <Typography color="text.primary" sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.6 }}>
                  Set your birth date and life expectancy to get personalized insights and progress tracking.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            borderTop: '1px solid rgba(255, 185, 93, 0.2)',
            mt: 8,
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            © {new Date().getFullYear()} Lifetime Timer. All rights reserved.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Box
              component={RouterLink}
              to="/privacy"
              sx={{ 
                color: 'text.secondary', 
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Privacy Policy
            </Box>
            <Box
              component={RouterLink}
              to="/feedback"
              sx={{ 
                color: 'text.secondary', 
                textDecoration: 'none',
                '&:hover': { color: 'primary.main' },
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Feedback
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export default Home; 